import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  Slide,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { apiFetch } from '../util/apiClient';

// Responsive sizing system matching Login and ResetPassword modals
// All dimensions scale proportionally using CSS clamp() with 25vw as the base unit
// Container dimensions - 552:409 aspect ratio (1.35:1)
const CONTAINER_WIDTH = 'clamp(318px, 25vw, 480px)';
const CONTAINER_HEIGHT = 'clamp(236px, calc(25vw * 0.741), 356px)';

// Padding - asymmetric (more left, less right)
const PADDING_LEFT = 'clamp(28px, calc(25vw * 0.087), 42px)'; // 42px at max
const PADDING_RIGHT = 'clamp(16px, calc(25vw * 0.051), 24px)'; // 24px at max
const PADDING_TOP = 'clamp(17px, calc(25vw * 0.054), 26px)';
const PADDING_BOTTOM = 'clamp(14px, calc(25vw * 0.045), 22px)';

// Font sizes
const TITLE_FONT_SIZE = 'clamp(21px, calc(25vw * 0.065), 31px)';
const BUTTON_FONT_SIZE = 'clamp(14px, calc(25vw * 0.032), 15px)'; // Increased min from 10px to 14px

// Element heights
const INPUT_HEIGHT = 'clamp(30px, calc(25vw * 0.094), 45px)';
const MESSAGE_HEIGHT = 'clamp(60px, calc(25vw * 0.188), 90px)'; // 2x the input height
const BUTTON_HEIGHT = 'clamp(22px, calc(25vw * 0.069), 33px)';

// Margins
const MARGIN_BETWEEN_INPUTS = 'clamp(17px, calc(25vw * 0.058), 28px)'; // Space between email and message
const MARGIN_BUTTON = 'clamp(14px, calc(25vw * 0.043), 21px)'; // Space before button

// Positioning
const BANNER_RIGHT = 'clamp(14px, calc(25vw * 0.043), 21px)';

interface ContactBannerProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
}

export const ContactBanner: React.FC<ContactBannerProps> = ({
  open,
  onClose,
  userEmail,
}) => {
  const [email, setEmail] = useState(userEmail);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Reset email and message when banner closes (without sending)
  React.useEffect(() => {
    if (open) {
      setEmail(userEmail);
      setMessage('');
    }
  }, [open, userEmail]);

  const handleSend = async () => {
    if (!message.trim() || !email.trim()) return;

    setSending(true);

    try {
      await apiFetch('/auth/contact/', {
        method: 'POST',
        body: JSON.stringify({ email, message }),
      });

      setSnackbar({
        open: true,
        message: 'Your message has been sent!',
        severity: 'success',
      });
      setMessage('');
      onClose();
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#003D36',
      borderRadius: 0,
      fontFamily: '"Inter", sans-serif',
      '& fieldset': {
        border: 'none',
        borderBottom: '2px solid #7BBCB3',
      },
      '&:hover fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
      '&.Mui-focused fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      '&::placeholder': {
        color: '#7BBCB3',
        opacity: 1,
        fontWeight: 600,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#7BBCB3',
      fontFamily: '"Inter", sans-serif',
      '&.Mui-focused': {
        color: '#7BBCB3',
      },
    },
  };

  return (
    <>
      <Slide direction='down' in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            right: BANNER_RIGHT,
            zIndex: 1300,
          }}
        >
          <ClickAwayListener onClickAway={onClose}>
            <Box
              sx={{
                backgroundColor: '#26675F',
                borderRadius: 2,
                borderTopRightRadius: 0,
                width: CONTAINER_WIDTH,
                height: CONTAINER_HEIGHT,
                pl: PADDING_LEFT,
                pr: PADDING_RIGHT,
                pt: PADDING_TOP,
                pb: PADDING_BOTTOM,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Typography
                sx={{
                  color: '#ffffff',
                  fontFamily: '"Futura PT", "Futura", sans-serif',
                  fontWeight: 700,
                  fontSize: TITLE_FONT_SIZE,
                }}
              >
                Contact CSI
              </Typography>

              <Box sx={{ flex: 1 }} />

              <TextField
                fullWidth
                placeholder='Your Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  ...inputStyles,
                  mb: MARGIN_BETWEEN_INPUTS,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                  },
                }}
              />

              <Box
                sx={{
                  width: '100%',
                  height: MESSAGE_HEIGHT,
                  position: 'relative',
                  mb: MARGIN_BUTTON,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#003D36',
                    overflow: 'auto',
                    padding: '8px 14px',
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder='Message'
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        fontFamily: '"Inter", sans-serif',
                        padding: 0,
                        '& fieldset': {
                          border: 'none',
                        },
                        '& textarea': {
                          color: '#ffffff',
                          fontFamily: '"Inter", sans-serif',
                          overflow: 'visible !important',
                          padding: 0,
                          '&::placeholder': {
                            color: '#7BBCB3',
                            opacity: 1,
                            fontWeight: 600,
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#7BBCB3',
                        fontFamily: '"Inter", sans-serif',
                        '&.Mui-focused': {
                          color: '#7BBCB3',
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    backgroundColor: '#7BBCB3',
                    pointerEvents: 'none',
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant='contained'
                onClick={handleSend}
                disabled={sending || !message.trim()}
                disableRipple
                sx={{
                  backgroundColor: 'rgba(9, 126, 113, 1) !important',
                  color: '#ffffff !important',
                  fontWeight: 600,
                  fontSize: BUTTON_FONT_SIZE,
                  height: BUTTON_HEIGHT,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: '#0a9485 !important',
                    boxShadow: 'none',
                  },
                  '&:active': {
                    backgroundColor: '#065a52 !important',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#4a7d78 !important',
                    color: 'rgba(255, 255, 255, 0.4) !important',
                  },
                }}
              >
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </ClickAwayListener>
        </Box>
      </Slide>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
